import React, { useEffect, useState } from "react";
import { API } from "@/lib/constants";
import { Loader2, ArrowRight, ChevronDown, ChevronRight, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";

// THE ONE LIST — one screen, ordered by what needs a person SOONEST.
// Real rows only (something specific owed by a named party by a named date). The
// cold-start breach collapses into one housekeeping roll-up at the top, worked through
// in place by giving each deal a one-line reason. No legend.
const KIND = {
  client_missed: { chip: "bg-red-100 text-red-700", word: "They didn't deliver" },
  client_due: { chip: "bg-blue-100 text-blue-700", word: "Waiting on them" },
  our_due: { chip: "bg-slate-100 text-slate-700", word: "On us" },
};

const ago = (iso) => {
  if (!iso) return "no contact logged";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "today"; if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return `${Math.floor(days / 30)} month${days >= 60 ? "s" : ""} ago`;
};
const when = (iso) => {
  if (!iso) return "no date";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00Z" : ""));
  const days = Math.floor((d - new Date()) / 86400000);
  const label = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  if (days < 0) return `${label} · overdue`;
  if (days === 0) return `${label} · today`;
  return label;
};

export default function OneList() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = () => fetch(`${API}/relationship/one-list`, { credentials: "include" })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then(setData).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  const rows = data?.rows;
  return (
    <div className="mx-auto max-w-4xl px-4 py-8" data-testid="one-list-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">The list</h1>
        <p className="mt-1 text-sm text-slate-500">Ordered by who needs you soonest. Open any row for the detail.</p>
      </div>

      {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="one-list-error">Couldn't load: {error}</div>}
      {!data && !error && <div className="flex items-center gap-2 py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>}

      {data?.breaches?.count > 0 && <BreachRollup breaches={data.breaches} onCleared={load} />}

      {rows && rows.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 py-16 text-center text-slate-400" data-testid="one-list-empty">
          Nothing specific is owed right now.
        </div>
      )}

      <div className="space-y-2">
        {rows && rows.map((r, i) => {
          const k = KIND[r.kind] || KIND.our_due;
          return (
            <button key={i} data-testid="one-list-row"
              onClick={() => r.client_id && navigate(`/clients/${r.client_id}`)}
              className="group flex w-full items-center gap-4 rounded-xl border border-slate-200 bg-white px-5 py-4 text-left transition-colors hover:border-slate-300 hover:bg-slate-50">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-slate-900" data-testid="row-who">{r.who || "Unknown client"}</span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${k.chip}`}>{k.word}</span>
                </div>
                <div className="mt-1 truncate text-sm text-slate-500" data-testid="row-last">
                  Last: {r.what_happened_last || "nothing recorded"} · {ago(r.when_last)}
                </div>
                <div className="mt-1 text-sm text-slate-700" data-testid="row-owed">
                  {r.owed_by === "them" ? "They owe" : "We owe"}: {r.owed_what}
                  {r.owed_by_when && <span className="ml-1 text-slate-400">· {when(r.owed_by_when)}</span>}
                  {!r.owed_by_when && r.owed_when_unclear &&
                    <span className="ml-1 font-medium text-amber-600" data-testid="row-date-unclear">· date unclear: "{r.owed_when_unclear}" — pin it</span>}
                </div>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-slate-500" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Cold-start housekeeping — one line, expandable, cleared in place with a reason.
function BreachRollup({ breaches, onCleared }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [count, setCount] = useState(breaches.count);
  const [reasons, setReasons] = useState({});
  const [saving, setSaving] = useState({});

  const expand = () => {
    setOpen((o) => !o);
    if (!items) {
      fetch(`${API}/relationship/commitment-rule`, { credentials: "include" })
        .then((r) => r.json()).then((d) => setItems(d.missing || [])).catch(() => setItems([]));
    }
  };

  const saveReason = async (opp) => {
    const reason = (reasons[opp.opportunity_id] || "").trim();
    if (!reason) return;
    setSaving((s) => ({ ...s, [opp.opportunity_id]: true }));
    try {
      await fetch(`${API}/relationship/opportunities/${opp.opportunity_id}/no-commitment-reason`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      setItems((prev) => prev.filter((x) => x.opportunity_id !== opp.opportunity_id));
      setCount((c) => Math.max(0, c - 1));
      onCleared && onCleared();
    } finally {
      setSaving((s) => ({ ...s, [opp.opportunity_id]: false }));
    }
  };

  if (count <= 0) return null;
  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50" data-testid="breach-rollup">
      <button onClick={expand} data-testid="breach-rollup-toggle"
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left">
        {open ? <ChevronDown className="h-4 w-4 text-amber-700" /> : <ChevronRight className="h-4 w-4 text-amber-700" />}
        <span className="flex-1 text-sm text-amber-900">
          <b>{count}</b> live deal{count !== 1 ? "s" : ""} have no client commitment recorded and no reason why
        </span>
        <span className="shrink-0 text-xs font-medium text-amber-700">Work through them</span>
      </button>

      {open && items && (
        <div className="max-h-96 space-y-2 overflow-y-auto border-t border-amber-200 px-5 py-3" data-testid="breach-list">
          {items.length === 0 && <p className="py-4 text-center text-sm text-amber-700">All cleared.</p>}
          {items.map((opp) => (
            <div key={opp.opportunity_id} className="rounded-lg bg-white p-3" data-testid="breach-item">
              <div className="text-sm font-medium text-slate-900">{opp.client_name || "Unknown client"}</div>
              <div className="mb-2 text-xs text-slate-500">{opp.name || opp.stage}</div>
              <div className="flex gap-2">
                <input data-testid="breach-reason-input"
                  value={reasons[opp.opportunity_id] || ""}
                  onChange={(e) => setReasons((r) => ({ ...r, [opp.opportunity_id]: e.target.value }))}
                  placeholder="Why is there no client commitment? (your words)"
                  className="flex-1 rounded border border-slate-300 px-2.5 py-1.5 text-sm" />
                <button data-testid="breach-reason-save" onClick={() => saveReason(opp)}
                  disabled={saving[opp.opportunity_id] || !(reasons[opp.opportunity_id] || "").trim()}
                  className="flex items-center gap-1 rounded bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-40">
                  {saving[opp.opportunity_id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Clear
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
